import React, { useEffect, useMemo } from "react";
import styles from "./HelpMePage.module.scss";
import { observer } from "mobx-react";
import { useStores } from "../../hooks/mobx";
import WelcomeStep from "./steps/WelcomeStep/WelcomeStep";
import RequirementStep from "./steps/RequirementStep/RequirementStep";
import RecipientDataStep from "./steps/RecipientDataStep/RecipientDataStep";
import EndStep from "./steps/EndStep/EndStep";

type THelpMePage = {};

export const HelpMePageLastStep: React.FC = () => {
  return (
    <div className={styles.HelpMePage} data-last={true}>
      <div className={styles.HelpMePage_Header}>
        <HelpMePageHeaderLastStep />
      </div>
      <div className={styles.HelpMePage_Divider} />
      <div className={styles.HelpMePage_Content}>
        <EndStep />
      </div>
    </div>
  );
};

const HelpMePage: React.FC<THelpMePage> = observer(() => {
  const { helpMeStore } = useStores();
  const { error, step, isWelcomePage, scrollError, resetFirstError } =
    helpMeStore;

  useEffect(() => {
    if (typeof window !== "undefined")
      window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, isWelcomePage]);

  useEffect(() => {
    if (typeof window !== "undefined" && scrollError) {
      const to = document?.getElementById(scrollError)?.offsetTop;
      if (to) {
        window.scrollTo({
          top: to - 20,
          behavior: "smooth",
        });
        resetFirstError();
      }
    }
  }, [scrollError]);

  if (error) {
    return <div style={{ background: "red", color: "white" }}>{error}</div>;
  }

  if (isWelcomePage) {
    return <WelcomeStep />;
  }

  let content;

  switch (step) {
    case 0:
      content = <RequirementStep />;
      break;
    case 1:
      content = <RecipientDataStep />;
      break;

    default:
      content = null;
  }

  return (
    <div className={styles.HelpMePage}>
      <div className={styles.HelpMePage_Header}>
        <HelpMePageHeader />
      </div>
      <div className={styles.HelpMePage_Divider} />
      <div className={styles.HelpMePage_Content}>
        {step < 2 && !isWelcomePage && (
          <div className={styles.HelpMePage_Content_Title}>
            {step === 0 && <h1>Данные для поиска</h1>}
            {step === 1 && <h1>О пациенте</h1>}
          </div>
        )}
        {content}
      </div>
      <div className={styles.HelpMePage_Navigation}>
        <HelpMePageNavigation />
      </div>
    </div>
  );
});

const HelpMePageHeaderLastStep: React.FC = () => {
  return (
    <div className={styles.HelpMePageHeader} data-step={3}>
      <HelpMePageHeaderItem
        text={"Подтверждение"}
        count={null}
        selected={false}
        colored={true}
        steps={3}
        last
      />
    </div>
  );
};

const HelpMePageHeader: React.FC<THelpMePage> = observer(() => {
  const { helpMeStore } = useStores();
  const { step } = helpMeStore;

  return (
    <div className={styles.HelpMePageHeader} data-step={step + 1}>
      <HelpMePageHeaderItem
        text={"Данные для поиска"}
        count={1}
        selected={step > 0}
        colored={step >= 0}
      />
      <HelpMePageHeaderItem
        text={"О пациенте"}
        count={2}
        selected={step > 1}
        colored={step >= 1}
      />
    </div>
  );
});

type THelpMePageHeaderItem = {
  text: string;
  count: number;
  selected: boolean;
  colored: boolean;
  last?: boolean;
  steps?: number;
};

const HelpMePageHeaderItem: React.FC<THelpMePageHeaderItem> = observer(
  ({ text, count, selected, colored, steps, last }) => {
    const { helpMeStore } = useStores();
    const { step, nextStep, setStep } = helpMeStore;

    const curStep = steps ? steps : step;

    const onPress = () => {
      if (count > curStep + 1) {
        nextStep();
      }
      if (count - 1 < curStep) {
        setStep(count - 1);
      }
    };

    return (
      <div
        className={styles.HelpMePageHeaderItem}
        data-selected={selected}
        data-colored={colored}
        onClick={onPress}
        data-step={curStep + 1}
        data-last={last}
      >
        {!selected && !!count && (
          <div
            className={styles.HelpMePageHeaderItem_Count}
            data-colored={colored}
          >
            {count}
          </div>
        )}
        {selected && (
          <div className={styles.HelpMePageHeaderItem_Check}>
            <img src="/static/images/helpMe/Check.svg" alt="check" />
          </div>
        )}
        <div
          className={styles.HelpMePageHeaderItem_Text}
          data-colored={colored}
          data-selected={selected}
          data-last={last}
        >
          {text}
        </div>
      </div>
    );
  }
);

const HelpMePageNavigation: React.FC = observer(() => {
  const { helpMeStore } = useStores();
  const { step, nextStep, prevStep, submitForm, isLoading } = helpMeStore;

  const buttons = useMemo(() => {
    if (step === 0) {
      return <NextButton text={"Следующий шаг"} onClick={nextStep} withArrow />;
    }
    if (step === 1) {
      return (
        <div className={styles.HelpMePageNavigation_Navigator}>
          <PrevButton text={"Предыдущий шаг"} onClick={prevStep} withArrow />
          <NextButton
            text={"Начать поиск"}
            onClick={submitForm}
            isLoading={isLoading}
          />
        </div>
      );
    }
    return null;
  }, [step, isLoading]);

  return <div className={styles.HelpMePageNavigation}>{buttons}</div>;
});

const PrevButton: React.FC<TButton> = ({ text, onClick, withArrow }) => {
  return (
    <div className={styles.PrevButton}>
      <div onClick={onClick} className={styles.PrevButton_Container}>
        {withArrow && (
          <div className={styles.PrevButton_Container_Arrow}>
            <img src="/static/images/helpMe/CaretLeft.svg" alt="left arrow" />
          </div>
        )}
        <div className={styles.PrevButton_Container_Button}>
          <span>{text}</span>
        </div>
      </div>
    </div>
  );
};

type TButton = {
  text: string;
  onClick: () => void;
  withArrow?: boolean;
  isLoading?: boolean;
};

const NextButton: React.FC<TButton> = observer(
  ({ text, onClick, withArrow, isLoading }) => {
    return (
      <div className={styles.NextButton}>
        <div
          onClick={isLoading ? null : onClick}
          className={styles.NextButton_Container}
        >
          {isLoading && (
            <img src="/static/images/icons/preloader.svg" alt={"preloader"} />
          )}
          {!isLoading && (
            <>
              <div className={styles.NextButton_Container_Button}>
                <span>{text}</span>
              </div>
              {withArrow && (
                <div className={styles.NextButton_Container_Arrow}>
                  <img
                    src="/static/images/helpMe/CaretRight.svg"
                    alt="right arrow"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }
);

export default HelpMePage;
